import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { InvitedUserDetails } from '@airwallet/shared-models/invited-user';
import { lastValueFrom } from 'rxjs';
import { HttpService, RequestTypes } from '@services/helper/http.service';
import { ExtendedSubscriptionPlanInfo, SubscriptionPlanType } from '@airwallet/shared-models/feature_access';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { Database, ref, set } from '@angular/fire/database';
import { CustomerDisplay, CustomerDisplayAndLocationAmount } from '@airwallet/shared-models/coupon';

@Injectable({
    providedIn: 'root'
})
export class AccountService {
    private database: Database = inject(Database);
    baseUrl: string = environment.baseUrl;
    enableQuickChange: WritableSignal<boolean> = signal(null);
    currentSubscriptionPlanInfo: WritableSignal<ExtendedSubscriptionPlanInfo> = signal(null);

    customerDisplay: WritableSignal<CustomerDisplay[]> = signal([]);
    loadingCustomers: WritableSignal<boolean> = signal(false);

    constructor(
        private authService: AuthService,
        private http: HttpClient,
        private httpService: HttpService,
        private featureService: FeatureService
    ) {}

    async updateMemberEmailSettings(uid: string, details: InvitedUserDetails): Promise<any> {
        try {
            const dbRef = ref(this.database, `customers/${uid}/details`);
            return set(dbRef, details);
        } catch (e) {
            console.error('Error updating team member email settings', e.message);
        }
    }

    async updateAccount(data: any): Promise<any> {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(this.http.put(`${this.baseUrl}/api_account`, data))
                .then((resp: any) => {
                    return resolve(resp);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async updateEmail(oldEmail: string, newEmail: string): Promise<any> {
        return this.httpService.dynamicHttp(`api_account/update_email`, RequestTypes.PUT, { body: { oldEmail, newEmail }, skipOwner: true });
    }

    async deleteAccount(signOut: boolean, onBehalf: string): Promise<void> {
        await this.httpService.dynamicHttp(`api_account/delete_account`, RequestTypes.DELETE, { onBehalf, skipOwner: true }).then(() => {
            if (signOut) this.authService.signOut();
        });
    }

    async getSubscriptionPlanInfo(): Promise<void> {
        const extendedSubscriptionPlanInfo: ExtendedSubscriptionPlanInfo = await this.httpService.dynamicHttp(`api_account/get_subscription_plan_info`, RequestTypes.GET);
        this.currentSubscriptionPlanInfo.set(extendedSubscriptionPlanInfo);
        this.enableQuickChange.set(extendedSubscriptionPlanInfo.enableQuickChange);
        return;
    }

    async confirmSubscriptionPlan(subscription_plan_type: SubscriptionPlanType, subscription_plan_interval: number): Promise<void> {
        const subscriptionPlanInfo: ExtendedSubscriptionPlanInfo = { ...this.currentSubscriptionPlanInfo(), subscription_plan_type, subscription_plan_interval };
        await this.httpService
            .dynamicHttp(`api_account/set_subscription_plan_info`, RequestTypes.POST, { body: { subscriptionPlanInfo } })
            .then(async () => {
                this.currentSubscriptionPlanInfo.set(subscriptionPlanInfo);
                await this.featureService.loadAccessMap();
            })
            .catch(err => {
                console.error('Error confirming subscription plan', err);
            });
        return;
    }

    /**
     * Fetches all customers and their locations, and sets the customerDisplay signal.
     */
    async getCustomersForDisplay(): Promise<CustomerDisplayAndLocationAmount> {
        this.loadingCustomers.set(true);
        const response: { allCustomers: CustomerDisplay[]; locationAmount: number } = await this.httpService.dynamicHttp<{ allCustomers: CustomerDisplay[]; locationAmount: number }>(
            'api_account/get_customer_and_locations',
            RequestTypes.GET
        );
        this.customerDisplay.set(response.allCustomers);
        this.loadingCustomers.set(false);
        return;
    }
}
